import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  CheckCircleIcon,
  DocumentIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import {
  DocumentPacket,
  documentSigningPdfUrl,
  documentViewUrl,
  getPacketByToken,
  requestSigningOtp,
  signPacket,
  verifySigningOtp,
} from '../services/documentService';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

type Step = 'email' | 'otp' | 'sign' | 'done';

const SigningPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');

  // OTP flow state
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [signingToken, setSigningToken] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(false);

  // Packet / signing state
  const [packet, setPacket] = useState<DocumentPacket | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  // Track each consent item's checked state by index
  const [consentChoices, setConsentChoices] = useState<Record<number, boolean>>({});
  const [signing, setSigning] = useState(false);

  // Shared error state
  const [error, setError] = useState('');
  const [autoAuthLoading, setAutoAuthLoading] = useState(true);

  const [pdfScrolled, setPdfScrolled] = useState<Record<string, boolean>>({});

  // If the client is already logged in, skip OTP and load the packet directly
  useEffect(() => {
    if (!token) { setAutoAuthLoading(false); return; }
    const jwt = localStorage.getItem('authToken');
    if (!jwt) { setAutoAuthLoading(false); return; }
    getPacketByToken(token)
      .then(data => {
        setPacket(data);
        if (data.consent_items && data.consent_items.length > 0) {
          const initial: Record<number, boolean> = {};
          data.consent_items.forEach((_, i) => { initial[i] = false; });
          setConsentChoices(initial);
        }
        setStep(data.status === 'signed' ? 'done' : 'sign');
      })
      .catch(() => {
        // JWT doesn't grant access to this packet (wrong client, expired, etc.) — fall back to OTP flow
      })
      .finally(() => setAutoAuthLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (autoAuthLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  if (!token) {
    return <ErrorScreen message="Invalid signing link." />;
  }

  // ── Step 1: email ────────────────────────────────────────────────────────────

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setRequesting(true);
    setError('');
    try {
      await requestSigningOtp(token, email.trim());
      setStep('otp');
      setResendCooldown(true);
      setTimeout(() => setResendCooldown(false), 30000);
    } catch (err: any) {
      setError(err.message || 'Failed to send code. Please try again.');
    } finally {
      setRequesting(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown) return;
    setError('');
    setResendCooldown(true);
    setTimeout(() => setResendCooldown(false), 30000);
    try {
      await requestSigningOtp(token, email.trim());
    } catch (err: any) {
      setError(err.message || 'Failed to resend code.');
    }
  };

  // ── Step 2: OTP verify ───────────────────────────────────────────────────────

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpCode.trim()) return;
    setVerifying(true);
    setError('');
    try {
      const { signing_token } = await verifySigningOtp(token, email.trim(), otpCode.trim());
      setSigningToken(signing_token);
      const data = await getPacketByToken(token, signing_token);
      setPacket(data);
      if (data.consent_items && data.consent_items.length > 0) {
        const initial: Record<number, boolean> = {};
        data.consent_items.forEach((_, i) => { initial[i] = false; });
        setConsentChoices(initial);
      }
      if (data.status === 'signed') {
        setStep('done');
      } else {
        setStep('sign');
      }
    } catch (err: any) {
      setError(err.message || 'Invalid or expired code.');
    } finally {
      setVerifying(false);
    }
  };

  // ── Step 3: sign ─────────────────────────────────────────────────────────────

  const allRequiredConsented = !packet?.consent_items || packet.consent_items.every(
    (item, i) => !item.required || (consentChoices[i] === true),
  );

  const handleSign = async () => {
    if (!packet || !acknowledged || !allRequiredConsented) return;
    setSigning(true);
    setError('');
    // Build consents payload: index → boolean for each consent item
    let consents: Record<string, boolean> | undefined;
    if (packet.consent_items && packet.consent_items.length > 0) {
      consents = {};
      packet.consent_items.forEach((_, i) => { consents![String(i)] = consentChoices[i] ?? false; });
    }
    try {
      await signPacket(token, consents, signingToken);
      setStep('done');
      const isLoggedIn = !!localStorage.getItem('authToken');
      setTimeout(() => {
        if (isLoggedIn) {
          navigate('/dashboard/client#documents', { replace: true });
        }
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Signing failed. Please try again.');
    } finally {
      setSigning(false);
    }
  };

  const fmtDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString(undefined, {
      month: 'long', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    }) : '—';

  // ── Render ───────────────────────────────────────────────────────────────────

  if (step === 'sign' && packet) {
    const hasConsent = packet.consent_items && packet.consent_items.length > 0;
    const allPdfsScrolled = packet.items.every(
      item => !item.pdf_file_path || pdfScrolled[item.id],
    );
    const canSign = acknowledged && allRequiredConsented && allPdfsScrolled;

    return (
      <div className="max-w-4xl mx-auto px-4 py-6 pb-24">
        {/* Packet title */}
        <div className="mb-6 pb-4 border-b">
          <h1 className="text-xl font-bold text-gray-900">{packet.title}</h1>
          <p className="text-sm text-gray-400 mt-0.5">Sent {fmtDate(packet.sent_at)}</p>
        </div>

        {/* Documents */}
        {packet.items.map((item) => {
          const docName = item.document?.name || item.document_id;
          const fileType = item.document?.file_type || 'pdf';
          const r2Url = item.view_url || documentViewUrl(packet.sign_token, item.document_id);
          const pdfUrl = item.pdf_file_path
            ? documentSigningPdfUrl(packet.sign_token, item.document_id)
            : null;
          return (
            <div key={item.id} className="mb-8">
              <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border rounded-t-lg">
                <div className="flex items-center gap-2">
                  <DocumentIcon className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">{docName}</span>
                </div>
                <a href={r2Url} download={docName} className="text-xs text-gray-400 hover:text-gray-600">Download</a>
              </div>
              {pdfUrl ? (
                <PdfViewer
                  url={pdfUrl}
                  alreadyScrolled={!!pdfScrolled[item.id]}
                  onScrollComplete={() => setPdfScrolled(prev => ({ ...prev, [item.id]: true }))}
                />
              ) : (
                <iframe
                  src={['docx', 'doc'].includes(fileType)
                    ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(r2Url)}`
                    : r2Url}
                  title={docName}
                  style={{ width: '100%', height: '70vh', display: 'block', border: '1px solid #e5e7eb', borderTop: 'none' }}
                />
              )}
              {pdfUrl && !pdfScrolled[item.id] && (
                <p className="text-xs text-amber-600 mt-1 text-center">Scroll to the bottom of the document to continue.</p>
              )}
            </div>
          );
        })}

        {/* Consent + sign section */}
        <div className="bg-white border rounded-xl shadow-sm p-6 space-y-4">
          {hasConsent && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-700">Please confirm before signing:</p>
              {packet.consent_items!.map((item, i) => (
                <label key={i} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consentChoices[i] ?? false}
                    onChange={e => setConsentChoices(prev => ({ ...prev, [i]: e.target.checked }))}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">{item.label}</span>
                </label>
              ))}
              <hr className="border-gray-100" />
            </div>
          )}
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={e => setAcknowledged(e.target.checked)}
              disabled={!allPdfsScrolled}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 flex-shrink-0 mt-0.5"
            />
            <span className={`text-sm ${allPdfsScrolled ? 'text-gray-700' : 'text-gray-400'}`}>
              I have read and agree to all documents above.
            </span>
          </label>
          {error && <ErrorBanner message={error} />}
          <button
            onClick={handleSign}
            disabled={!canSign || signing}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg disabled:opacity-50 transition-colors"
          >
            {signing ? <Spinner /> : <CheckCircleIcon className="w-5 h-5" />}
            Confirm & Sign
          </button>
          {!allPdfsScrolled && (
            <p className="text-xs text-center text-gray-400">Read all documents above to unlock signing.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-lg">

        {/* ── Step 1: Email ── */}
        {step === 'email' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h1 className="text-xl font-bold text-gray-900 mb-1">Review & Sign Documents</h1>
            <p className="text-sm text-gray-500 mb-6">
              Enter the email address associated with your account to receive a verification code.
            </p>
            <form onSubmit={handleRequestOtp} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              {error && <ErrorBanner message={error} />}
              <button
                type="submit"
                disabled={requesting || !email.trim()}
                className="w-full py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {requesting && <Spinner />}
                Send Verification Code
              </button>
            </form>
          </div>
        )}

        {/* ── Step 2: OTP ── */}
        {step === 'otp' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h1 className="text-xl font-bold text-gray-900 mb-1">Enter your code</h1>
            <p className="text-sm text-gray-500 mb-6">
              A 6-digit code was sent to <span className="font-medium text-gray-700">{email}</span>. It expires in 10 minutes.
            </p>
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Verification code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  required
                  autoFocus
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-center tracking-widest text-lg font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              {error && <ErrorBanner message={error} />}
              <button
                type="submit"
                disabled={verifying || otpCode.length < 6}
                className="w-full py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {verifying && <Spinner />}
                Verify Code
              </button>
            </form>
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={handleResend}
                disabled={resendCooldown}
                className="text-sm text-primary-600 hover:underline disabled:text-gray-400 disabled:no-underline"
              >
                {resendCooldown ? 'Code sent — resend available in 30s' : 'Resend code'}
              </button>
              <span className="text-gray-300 mx-2">·</span>
              <button
                type="button"
                onClick={() => { setStep('email'); setOtpCode(''); setError(''); }}
                className="text-sm text-gray-500 hover:underline"
              >
                Change email
              </button>
            </div>
          </div>
        )}

        {/* ── Step 4: Done ── */}
        {step === 'done' && (
          <div className="bg-white rounded-xl shadow-lg p-10 text-center">
            <CheckCircleSolid className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Signed successfully</h2>
            <p className="text-gray-500 text-sm">
              Your signature has been recorded.
            </p>
            <p className="text-gray-400 text-sm mt-2">You can close this tab.</p>
          </div>
        )}

      </div>
    </div>
  );
};

// ── PDF Viewer with scroll enforcement ───────────────────────────────────────

const PDF_VIEWER_HEIGHT = 680;

const PdfViewer: React.FC<{
  url: string;
  alreadyScrolled: boolean;
  onScrollComplete: () => void;
}> = ({ url, alreadyScrolled, onScrollComplete }) => {
  const [numPages, setNumPages] = useState(0);
  const [containerWidth, setContainerWidth] = useState(780);
  const scrollRef = useRef<HTMLDivElement>(null);

  const measureWidth = useCallback(() => {
    if (scrollRef.current) {
      setContainerWidth(scrollRef.current.clientWidth - 32);
    }
  }, []);

  useEffect(() => {
    measureWidth();
    window.addEventListener('resize', measureWidth);
    return () => window.removeEventListener('resize', measureWidth);
  }, [measureWidth]);

  const checkScrolled = useCallback(() => {
    if (alreadyScrolled || !scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    if (scrollHeight <= clientHeight + 10 || scrollTop + clientHeight >= scrollHeight * 0.9) {
      onScrollComplete();
    }
  }, [alreadyScrolled, onScrollComplete]);

  const handleLoad = ({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    // Give the DOM a tick to render all pages, then check if scrolling is even needed
    setTimeout(checkScrolled, 300);
  };

  return (
    <div
      ref={scrollRef}
      onScroll={checkScrolled}
      style={{
        height: PDF_VIEWER_HEIGHT,
        overflowY: 'auto',
        border: '1px solid #e5e7eb',
        borderTop: 'none',
        borderRadius: '0 0 8px 8px',
        backgroundColor: '#f9fafb',
      }}
    >
      <Document
        file={url}
        onLoadSuccess={handleLoad}
        loading={
          <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
            Loading document...
          </div>
        }
        error={
          <div className="flex items-center justify-center h-64 text-red-400 text-sm">
            Failed to load document.
          </div>
        }
      >
        {Array.from({ length: numPages }, (_, i) => (
          <div key={i + 1} className="flex justify-center py-2">
            <Page
              pageNumber={i + 1}
              width={containerWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </div>
        ))}
      </Document>
    </div>
  );
};

const Spinner: React.FC = () => (
  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
);

const ErrorBanner: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
    <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
    {message}
  </div>
);

const ErrorScreen: React.FC<{ message: string }> = ({ message }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
    <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
      <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to load documents</h2>
      <p className="text-gray-600 text-sm">{message}</p>
    </div>
  </div>
);

export default SigningPage;
